import styled from "@emotion/styled";
import { NATIVE_SOL_TOKEN_INFO } from "@stepfinance/common";
import type { NFTEscrowAccount } from "@stepfinance/nft";
import type { StepColumnsType } from "@stepfinance/react";
import {
  CheckboxFilter,
  formatBalance,
  IconTextContainer,
  makeSimpleFilters,
  PlatformIcon,
  StepTable,
  TokenIconText,
} from "@stepfinance/react";
import type { TablePaginationConfig } from "antd";
import type { FilterValue } from "antd/lib/table/interface";
import type { FC } from "react";
import { useEffect, useState } from "react";

import { useLanguageContext } from "../../../contexts/language/language";
import { isPaginationHidden, useLocalStorageState } from "../../../utils/utils";
import { NavigationLink, PAGE_SIZE_OPTIONS } from "../../shared";
import CellValue from "../../Table/CellValue";
import { TokenIcon } from "../../TokenIcon";
import { TokenIconContainer } from "../Balances/SpotAssets";
import { ChevronCollapse } from "../ChevronCollapse/ChevronCollapse";

type NFTEscrowAccountTableProps = {
  totalValue: number;
  escrowAccounts: NFTEscrowAccount[];
};

export const NFTEscrowAccountsTable: FC<NFTEscrowAccountTableProps> = ({ escrowAccounts, totalValue }) => {
  const {
    currentLanguageData: {
      common,
      pages: {
        dashboard: {
          modules: { nftMarkets },
        },
      },
    },
  } = useLanguageContext();

  const [pagination, setPagination] = useState<TablePaginationConfig>({});
  const [filteredInfo, setFilteredInfo] = useState<Record<string, FilterValue | null>>({});
  const [paginationSetting, setPaginationSetting] = useLocalStorageState(
    "starAtlasOpenOrdersPaginationSizeSetting",
    PAGE_SIZE_OPTIONS.m[0],
  );

  useEffect(() => {
    setPagination({
      hideOnSinglePage: isPaginationHidden(escrowAccounts.length, PAGE_SIZE_OPTIONS.m),
      showSizeChanger: true,
      pageSizeOptions: PAGE_SIZE_OPTIONS.m,
      pageSize: parseInt(paginationSetting),
    });
  }, [escrowAccounts.length, paginationSetting]);

  const columns: StepColumnsType<NFTEscrowAccount> = [
    {
      title: common.platform,
      dataIndex: "kind",
      key: "kind",
      filters: makeSimpleFilters(escrowAccounts.map((order) => order.platform)),
      filterDropdown: (props) => <CheckboxFilter {...props} />,
      onFilter: (value, record) => record.platform.startsWith(value as string),
      filteredValue: filteredInfo.kind ?? null,
      render(_, { platform }) {
        return (
          <IconTextContainer>
            <PlatformIcon names={[platform]} />
            <TokenIconText bold>{platform}</TokenIconText>
          </IconTextContainer>
        );
      },
      sorter: (a, b) => a.platform.localeCompare(b.platform),
    },
    {
      title: common.token,
      dataIndex: "token",
      key: "token",
      className: "column-string",
      render: (_, record) => (
        <TokenIconContainer>
          <TokenIcon mintAddress={record.tokenMintAddress} />
          <TokenIconText bold>
            <NavigationLink
              href={`https://analytics.step.finance/defionsolana/token?mint=${record.tokenMintAddress}`}
              target="_blank"
            >
              {record.tokenSymbol}
            </NavigationLink>
          </TokenIconText>
        </TokenIconContainer>
      ),
    },
    {
      title: common.balance,
      dataIndex: "balance",
      key: "balance",
      className: "column-string",
      render(_, record) {
        return <CellValue cellType="number">{formatBalance(record.balance, NATIVE_SOL_TOKEN_INFO.decimals)}</CellValue>;
      },
    },
    {
      title: common.value,
      dataIndex: "value",
      key: "value",
      className: "column-string",
      render(_, record) {
        return (
          <CellValue cellType="userPositionValue" fontWeight="bold">
            {record.value}
          </CellValue>
        );
      },
    },
  ];

  return (
    <NFTEscrowAccountsContainer>
      <ChevronCollapse title={nftMarkets.escrowAccounts} value={totalValue} isCollapseChild>
        <StepTable
          onChange={(pagination, filters) => {
            setPaginationSetting(pagination.pageSize ? pagination.pageSize.toString() : PAGE_SIZE_OPTIONS.m[0]);
            setFilteredInfo(filters);
          }}
          pagination={pagination}
          columns={columns}
          dataSource={escrowAccounts}
        />
      </ChevronCollapse>
    </NFTEscrowAccountsContainer>
  );
};

const NFTEscrowAccountsContainer = styled.div`
  margin-top: 20px;
`;
